"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, Building2, Star, Code as Code2, Compass, Link2, FileText, Layers, Navigation, ChartBar as BarChart3, Smartphone, CircleCheck as CheckCircle2, ArrowRight, Sparkles } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const localServices = [{
  id: "gbp-opt",
  title: "Google Business Profile Optimization",
  description: "Complete setup, category alignment, and ongoing tuning of your GBP to secure top rankings in the Google Local Map Pack.",
  badge: "Map Pack Focus",
  icon: MapPin,
  deliverable: "Primary category & photo updates"
}, {
  id: "local-citations",
  title: "Local Citation Audit & Building",
  description: "Audit and resolve inconsistent NAP (Name, Address, Phone) data across high-authority UK directories to build local trust signals.",
  badge: "Trust & NAP Consistency",
  icon: Building2,
  deliverable: "Clean directory footprint"
}, {
  id: "review-mgmt",
  title: "Review Generation & Strategy",
  description: "Turn customer satisfaction into search dominance with strategic review acquisition frameworks and response protocols.",
  badge: "Reputation Power",
  icon: Star,
  deliverable: "Automated feedback loops"
}, {
  id: "local-schema",
  title: "Local Schema Markup Implementation",
  description: "Embed tailored JSON-LD structured data (LocalBusiness, GeoCoordinates, OpeningHours) into your site code for rich snippets.",
  badge: "Technical SEO",
  icon: Code2,
  deliverable: "JSON-LD schema validation"
}, {
  id: "geo-onpage",
  title: "Geo-Targeted On-Page Optimization",
  description: "Optimize title tags, meta descriptions, localized headers, and service copy for high-intent city and regional queries.",
  badge: "On-Page Focus",
  icon: Compass,
  deliverable: "Location-specific metadata"
}, {
  id: "local-links",
  title: "Local Link Building & Digital PR",
  description: "Earn authoritative contextual backlinks from regional news outlets, local industry hubs, and community sponsorships.",
  badge: "Authority Links",
  icon: Link2,
  deliverable: "High-relevance local links"
}, {
  id: "hyperlocal-content",
  title: "Hyper-Local Content Creation",
  description: "Draft comprehensive regional landing pages and location guides designed to convert ready-to-buy local prospects.",
  badge: "High-Converting Content",
  icon: FileText,
  deliverable: "Custom regional copy"
}, {
  id: "multilocation-seo",
  title: "Multi-Location SEO Management",
  description: "Structured SEO architecture engineered for businesses operating multiple physical branches or service territories.",
  badge: "Multi-Branch Growth",
  icon: Layers,
  deliverable: "Scalable location trees"
}, {
  id: "apple-bing-maps",
  title: "Apple Maps & Bing Places Tuning",
  description: "Extend your search reach beyond Google with complete profiles on Apple Maps, Bing Places, and voice nav platforms.",
  badge: "Cross-Platform Maps",
  icon: Navigation,
  deliverable: "Voice search readiness"
}, {
  id: "competitor-serp",
  title: "Local Competitor & SERP Analysis",
  description: "Uncover competitor map pack strategies, review velocity, and localized backlink profiles to pinpoint ranking gaps.",
  badge: "Competitive Intelligence",
  icon: BarChart3,
  deliverable: "Actionable SERP report"
}, {
  id: "mobile-voice-seo",
  title: "Mobile & Voice Search Tuning",
  description: "Optimize for 'near me' mobile searches and voice assistants with conversational queries and rapid mobile response.",
  badge: "'Near Me' Optimization",
  icon: Smartphone,
  deliverable: "Mobile speed & intent alignment"
}, {
  id: "local-reporting",
  title: "Transparent Local SEO Audits",
  description: "Clear, zero-fluff monthly reporting showing map pack movements, direction requests, phone calls, and organic leads.",
  badge: "Honest Measurement",
  icon: Sparkles,
  deliverable: "Monthly video walkthroughs"
}];
export default function Localseo() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="localseoservicesgrid" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden">
      <div className="aria-hidden:hidden absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-primary/5 blur-[120px] rounded-full pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold tracking-wide uppercase mb-4 border border-border">
            <Sparkles className="size-3.5 text-accent" />
            <span>Direct Consultant Expertise</span>
          </div>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground font-serif">
            Comprehensive Local SEO Services
          </h2>

          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed font-sans">
            You work directly with me—Yassine Essaadi. I personally execute tailored local SEO strategies designed to capture high-intent searches in your target towns and regions.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {localServices.map((service, index) => {
          const IconComponent = service.icon;
          return <div key={service.id} data-index={index} className="h-full">
                <motion.div variants={itemVariants} className="h-full">
                  <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-[box-shadow,border-color] duration-300 hover:border-primary/40 flex flex-col justify-between overflow-hidden group">
                    <div className="bg-primary text-primary-foreground p-6 flex items-start justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2.5 rounded-lg bg-primary-foreground/10 text-primary-foreground border border-primary-foreground/20">
                          <IconComponent className="size-5 shrink-0" />
                        </div>
                        <Badge variant="secondary" className="bg-primary-foreground/15 text-primary-foreground border-transparent text-[13px] font-medium">
                          {service.badge}
                        </Badge>
                      </div>
                    </div>

                    <CardHeader className="pt-6 pb-2 px-6">
                      <CardTitle className="text-xl font-semibold text-card-foreground group-hover:text-primary transition-colors duration-200 font-serif leading-snug">
                        {service.title}
                      </CardTitle>
                      <CardDescription className="text-sm text-muted-foreground mt-2 leading-relaxed font-sans">
                        {service.description}
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="py-4 px-6 mt-auto">
                      <div className="pt-4 border-t border-border flex items-center gap-2 text-xs font-medium text-foreground">
                        <CheckCircle2 className="size-4 text-accent shrink-0" />
                        <span>Deliverable: {service.deliverable}</span>
                      </div>
                    </CardContent>

                    <CardFooter className="pt-2 pb-6 px-6">
                      <Button asChild variant="ghost" size="sm" className="w-full justify-between text-primary hover:text-primary hover:bg-muted font-medium text-xs px-3 py-2 rounded-md group/btn">
                        <Link to="/LocalSEOPage">
                          <span>Explore Local Strategy</span>
                          <ArrowRight className="size-3.5 transition-transform duration-200 group-hover/btn:translate-x-1" />
                        </Link>
                      </Button>
                    </CardFooter>
                  </Card>
                </motion.div>
              </div>;
        })}
        </motion.div>

        <div className="mt-16 bg-muted/60 border border-border rounded-2xl p-8 lg:p-10 text-center max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="text-left max-w-xl">
            <h3 className="text-xl sm:text-2xl font-semibold text-foreground font-serif">
              Ready to dominate local search in your area?
            </h3>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
              Book a direct consultation with me. No account managers, no automated templates—just a direct, hands-on local SEO audit and roadmap.
            </p>
          </div>
          <div className="flex shrink-0 gap-3 w-full md:w-auto">
            <Button asChild size="lg" className="w-full md:w-auto bg-primary text-primary-foreground hover:bg-primary/90 font-medium">
              <Link to="/ContactPage">
                Book a Free SEO Consultation
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}